import { VideoData, Frame } from '../types/VideoData';
import { PROCESSING_CONFIG } from '../constants/processingConfig';

export class VideoLoader {
  /**
   * Load and validate a video file (lightweight - no frame extraction)
   */
  async loadVideo(file: File, view: 'side' | 'front'): Promise<VideoData> {
    const videoElement = await this.createVideoElement(file);
    const metadata = await this.extractMetadata(videoElement);
    
    // Validate basic requirements
    this.validateBasicRequirements(metadata);
    
    // Don't extract all frames - this would use too much memory!
    // Frames will be extracted on-demand by DiscreteFrameSampler
    
    // Keep video element for on-demand frame extraction
    // Note: We don't revoke the URL here since we'll need it for frame extraction
    
    return {
      id: this.generateId(),
      frames: [], // Empty - frames extracted on-demand
      fps: PROCESSING_CONFIG.targetFPS,
      resolution: metadata.resolution,
      duration: metadata.duration,
      view,
      videoElement, // Store video element for on-demand extraction
      videoUrl: videoElement.src // Store URL for cleanup later
    };
  }
  
  /**
   * Create video element from file
   */
  private async createVideoElement(file: File): Promise<HTMLVideoElement> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        resolve(video);
      };
      
      video.onerror = () => {
        reject(new Error('Failed to load video file'));
      };
    });
  }
  
  /**
   * Extract video metadata
   */
  private async extractMetadata(video: HTMLVideoElement): Promise<{
    duration: number;
    resolution: { width: number; height: number };
    fps: number;
  }> {
    return {
      duration: video.duration,
      resolution: {
        width: video.videoWidth,
        height: video.videoHeight
      },
      fps: 30 // Browser doesn't expose FPS directly, we'll normalize to target
    };
  }
  
  /**
   * Validate basic video requirements
   */
  private validateBasicRequirements(metadata: {
    duration: number;
    resolution: { width: number; height: number };
  }): void {
    const { minDuration, maxDuration, minResolution } = PROCESSING_CONFIG;
    
    if (metadata.duration < minDuration) {
      throw new Error(`Video too short. Minimum ${minDuration} seconds required.`);
    }
    
    if (metadata.duration > maxDuration) {
      throw new Error(`Video too long. Maximum ${maxDuration} seconds allowed.`);
    }
    
    // Check resolution - works for both portrait and landscape
    const minDimension = Math.min(metadata.resolution.width, metadata.resolution.height);
    const requiredMinDimension = Math.min(minResolution.width, minResolution.height);
    const totalPixels = metadata.resolution.width * metadata.resolution.height;
    const requiredPixels = minResolution.width * minResolution.height;
    
    if (minDimension < requiredMinDimension || totalPixels < requiredPixels) {
      const orientation = metadata.resolution.width > metadata.resolution.height ? 'landscape' : 'portrait';
      throw new Error(
        `Resolution too low. Your video is ${metadata.resolution.width}x${metadata.resolution.height} (${orientation}). ` +
        `Minimum dimension required: ${requiredMinDimension}px, minimum total pixels: ${requiredPixels.toLocaleString()}.`
      );
    }
  }
  
  /**
   * Extract a single frame at specific timestamp (memory efficient)
   */
  async extractSingleFrame(
    video: HTMLVideoElement,
    timestamp: number,
    frameIndex: number
  ): Promise<Frame> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    // Downscale to max 1280x720 to balance quality and memory usage
    // Pose estimation works best at this resolution
    const maxWidth = 1280;
    const maxHeight = 720;
    let targetWidth = video.videoWidth;
    let targetHeight = video.videoHeight;
    
    // Calculate scaling factor to fit within max dimensions
    const scaleW = maxWidth / targetWidth;
    const scaleH = maxHeight / targetHeight;
    const scale = Math.min(scaleW, scaleH, 1.0); // Don't upscale
    
    if (scale < 1.0) {
      targetWidth = Math.floor(targetWidth * scale);
      targetHeight = Math.floor(targetHeight * scale);
    }
    
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    // Seek to timestamp
    await this.seekVideo(video, timestamp);
    
    // Draw frame to canvas (scaled if needed)
    ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
    
    // Get image data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    return {
      imageData,
      timestamp,
      index: frameIndex
    };
  }

  /**
   * Extract evenly-spaced frames from video
   */
  async extractFrames(video: HTMLVideoElement, frameCount: number = 40): Promise<Frame[]> {
    const frames: Frame[] = [];
    const frameDuration = video.duration / frameCount;
    
    for (let i = 0; i < frameCount; i++) {
      const timestamp = i * frameDuration;
      const frame = await this.extractSingleFrame(video, timestamp, i);
      frames.push(frame);
    }
    
    return frames;
  }
  
  /**
   * Seek video to specific timestamp
   */
  private seekVideo(video: HTMLVideoElement, timestamp: number): Promise<void> {
    return new Promise((resolve) => {
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        resolve();
      };
      
      video.addEventListener('seeked', onSeeked);
      video.currentTime = timestamp;
    });
  }
  
  
  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
